import supabase from './supabase'

// ─── AUTH ────────────────────────────────────────────────────────────────────

export const signUp = async (email, password, role, name) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        name: name.trim(),
        role: role
      }
    }
  })
  
  if (error) throw error
  return data
}

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
  return data
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export const getSession = async () => {
  const { data, error } = await supabase.auth.getSession()
  if (error) throw error
  return data.session
}

// ─── PROFILES ────────────────────────────────────────────────────────────────

export const upsertProfile = async (profileData) => {
  const { data, error } = await supabase
    .from('profiles_1774885039136')
    .upsert(
      { ...profileData, updated_at: new Date().toISOString() },
      { onConflict: 'id' }
    )
    .select()
    .single()

  if (error) throw error
  return data
}

export const getProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles_1774885039136')
    .select('*')
    .eq('id', userId)
    .single()

  if (error) throw error
  return data
}

export const updateProfile = async (userId, updates) => {
  const { data, error } = await supabase
    .from('profiles_1774885039136')
    .upsert(
      { id: userId, ...updates, updated_at: new Date().toISOString() },
      { onConflict: 'id' }
    )
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Find a disciple profile by email address.
 * Uses the "Anyone can search disciple profiles" RLS policy.
 */
export const findProfileByEmail = async (email) => {
  const normalized = email.trim().toLowerCase()
  const { data, error } = await supabase
    .from('profiles_1774885039136')
    .select('id, name, email, role')
    .eq('email', normalized)
    .eq('role', 'disciple')
    .maybeSingle()

  if (error) throw error
  return data
}

// ─── DISCIPLESHIP PAIRS ───────────────────────────────────────────────────────

export const createPair = async (leaderId, discipleId = null) => {
  const { data, error } = await supabase
    .from('discipleship_pairs_1774885039136')
    .insert({ leader_id: leaderId, disciple_id: discipleId })
    .select()
    .single()

  if (error) throw error
  return data
}

export const getMyPair = async (userId, role) => {
  const column = role === 'leader' ? 'leader_id' : 'disciple_id'
  const { data, error } = await supabase
    .from('discipleship_pairs_1774885039136')
    .select(`*, leader:leader_id(*), disciple:disciple_id(*)`)
    .eq(column, userId)
    .eq('status', 'active')
    .maybeSingle()

  if (error) throw error
  return data
}

export const linkDiscipleToPair = async (leaderId, discipleId) => {
  const { data: existing, error: fetchErr } = await supabase
    .from('discipleship_pairs_1774885039136')
    .select('id, disciple_id')
    .eq('leader_id', leaderId)
    .eq('status', 'active')
    .maybeSingle()

  if (fetchErr) throw fetchErr

  if (existing) {
    const { data, error } = await supabase
      .from('discipleship_pairs_1774885039136')
      .update({ disciple_id: discipleId })
      .eq('id', existing.id)
      .select(`*, leader:leader_id(*), disciple:disciple_id(*)`)
      .single()

    if (error) throw error
    return data
  } else {
    const { data, error } = await supabase
      .from('discipleship_pairs_1774885039136')
      .insert({ leader_id: leaderId, disciple_id: discipleId })
      .select(`*, leader:leader_id(*), disciple:disciple_id(*)`)
      .single()

    if (error) throw error
    return data
  }
}

export const unlinkDisciple = async (pairId) => {
  const { data, error } = await supabase
    .from('discipleship_pairs_1774885039136')
    .update({ disciple_id: null })
    .eq('id', pairId)
    .select()
    .single()

  if (error) throw error
  return data
}

export const updatePairStatus = async (pairId, status) => {
  const { data, error } = await supabase
    .from('discipleship_pairs_1774885039136')
    .update({
      status,
      ...(status === 'completed' ? { completed_at: new Date().toISOString() } : {}),
    })
    .eq('id', pairId)
    .select()
    .single()

  if (error) throw error
  return data
}

// ─── MEETINGS ─────────────────────────────────────────────────────────────────

export const getMeetings = async (pairId) => {
  const { data, error } = await supabase
    .from('meetings_1774885039136')
    .select('*')
    .eq('pair_id', pairId)
    .order('scheduled_date', { ascending: true })

  if (error) throw error
  return data
}

export const createMeeting = async (pairId, createdBy, meetingData) => {
  const { data, error } = await supabase
    .from('meetings_1774885039136')
    .insert({ pair_id: pairId, created_by: createdBy, ...meetingData })
    .select()
    .single()

  if (error) throw error
  return data
}

export const updateMeeting = async (meetingId, updates) => {
  const { data, error } = await supabase
    .from('meetings_1774885039136')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', meetingId)
    .select()
    .single()

  if (error) throw error
  return data
}

export const deleteMeeting = async (meetingId) => {
  const { error } = await supabase
    .from('meetings_1774885039136')
    .delete()
    .eq('id', meetingId)

  if (error) throw error
}

// ─── MODULE PROGRESS ─────────────────────────────────────────────────────────

export const getModuleProgress = async (pairId) => {
  const { data, error } = await supabase
    .from('module_progress_1774885039136')
    .select('*')
    .eq('pair_id', pairId)
    .order('week_number', { ascending: true })

  if (error) throw error
  return data
}

export const getWeekProgress = async (pairId, weekNumber) => {
  const { data, error } = await supabase
    .from('module_progress_1774885039136')
    .select('*')
    .eq('pair_id', pairId)
    .eq('week_number', weekNumber)
    .maybeSingle()

  if (error) throw error
  return data
}

export const upsertWeekProgress = async (pairId, weekNumber, updates) => {
  const { data, error } = await supabase
    .from('module_progress_1774885039136')
    .upsert(
      {
        pair_id: pairId,
        week_number: weekNumber,
        ...updates,
        updated_at: new Date().toISOString()
      },
      { onConflict: 'pair_id,week_number' }
    )
    .select()
    .single()

  if (error) throw error
  return data
}

export const completeWeek = async (pairId, weekNumber) => {
  return upsertWeekProgress(pairId, weekNumber, {
    status: 'completed',
    completed_at: new Date().toISOString(),
  })
}

// ─── SHARED NOTES ─────────────────────────────────────────────────────────────

export const getSharedNote = async (pairId, weekNumber) => {
  const { data, error } = await supabase
    .from('shared_notes_1774885039136')
    .select('*')
    .eq('pair_id', pairId)
    .eq('week_number', weekNumber)
    .maybeSingle()

  if (error) throw error
  return data
}

export const upsertSharedNote = async (pairId, weekNumber, content, userId) => {
  const { data, error } = await supabase
    .from('shared_notes_1774885039136')
    .upsert(
      {
        pair_id: pairId,
        week_number: weekNumber,
        content,
        last_edited_by: userId,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'pair_id,week_number' }
    )
    .select()
    .single()

  if (error) throw error
  return data
}

// ─── PRAYER REQUESTS ─────────────────────────────────────────────────────────

export const getPrayerRequests = async (pairId) => {
  const { data, error } = await supabase
    .from('prayer_requests_1774885039136')
    .select(`*, submitted_by_profile:submitted_by(name, role)`)
    .eq('pair_id', pairId)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data
}

export const createPrayerRequest = async (pairId, userId, title, description) => {
  const { data, error } = await supabase
    .from('prayer_requests_1774885039136')
    .insert({ pair_id: pairId, submitted_by: userId, title, description })
    .select()
    .single()

  if (error) throw error
  return data
}

export const markPrayerAnswered = async (prayerId, isAnswered) => {
  const { data, error } = await supabase
    .from('prayer_requests_1774885039136')
    .update({
      is_answered: isAnswered,
      answered_at: isAnswered ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', prayerId)
    .select()
    .single()

  if (error) throw error
  return data
}

export const deletePrayerRequest = async (prayerId) => {
  const { error } = await supabase
    .from('prayer_requests_1774885039136')
    .delete()
    .eq('id', prayerId)

  if (error) throw error
}